<script setup>
import TheSelectAdAccounts from '@shared-components/ui/selects/TheSelectAdAccounts.vue'
import {defineEmits, defineExpose, defineModel, defineProps} from 'vue'
import {useAdAccountTopUpRequestStore} from '@/views/ad-account-requests/top-up/AdAccountTopUpRequestStore'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import AlertInfoSimple from '@shared-components/ui/alerts/AlertInfoSimple.vue'
import TheSwitcher from '@shared-components/ui/inputs/TheSwitcher.vue'
import {formatSum} from '@shared/utils/helpers'
import CompanyBalanceErrorAlert from '@/views/ad-account-requests/top-up/components/CompanyBalanceErrorAlert.vue'
import DisabledAccountErrorAlert from '@shared-components/pages/requests/manage-funds/top-up/DisabledAccountErrorAlert.vue'
import {useWithdrawRequestForm} from '@shared/composables/pages/requests/manage-funds/useWithdrawRequestForm'

const {createAdAccountTopUpRequest} = useAdAccountTopUpRequestStore()
const {canUseTopUpAsap} = storeToRefs(useAuthenticationStore())
const {pingBackend} = useDefaultStore()

const companyModelValue = defineModel('company', {required: true})

const emit = defineEmits(['created', 'open-invoice-modal'])
const props = defineProps({
  adAccount: {
    type: Object,
  },
  isAccountSelectBySearch: {
    type: Boolean,
    default: false,
  },
})

const {
  UNRESTRICTED_BALANCE_ENTITY_TYPES,
  formData,
  error,
  loading,
  selectAdAccountRef,
  canSubmit,
  isCompanyBalanceError,
  removeItem,
  handleGetOption,
  onAdAccountChanged,
  onAmountChange,
  setAmount,
  onWithdrawFullBalance,
  openInvoiceModal,
  handleCreate,
  addNewItem,
  resetFormAndReloadAccounts,
  isAmountDisabled,
  isFullBalanceWithdraw,
} = useWithdrawRequestForm({
  companyModelValue,
  adAccount: props.adAccount,
  emit,
  createAdAccountTopUpRequest,
  pingBackend,
})

defineExpose({handleCreate, canSubmit, resetFormAndReloadAccounts})
</script>

<template>
  <div class="alert alert-grey-with-border custom mb-4 pb-5 pt-4" v-for="(item, index) in formData" :key="item._uid">
    <div class="row">
      <div class="col">
        <label class="custom fs-4 lh-1" :for="`withdraw-request-account-${index}`">Ad Account *</label>
        <the-select-ad-accounts
          v-model="item.entity_id"
          :list-by-search="isAccountSelectBySearch"
          :payload="{with_can_has_top_up: 1, company_hash: companyModelValue, purpose: 'topup'}"
          @changed="onAdAccountChanged($event, index)"
          @select-option="handleGetOption($event, index)"
          :disabled="loading"
          :id="`withdraw-request-account-${index}`"
          placeholder="Select Ad Account"
          ref="selectAdAccountRef"
          :preselect-account="index === 0 ? props.adAccount : null"
          required
        />
      </div>
      <div class="col-auto w-180-px pl-0">
        <label class="custom fs-4 lh-1" :for="`withdraw-request-amount-${index}`">Amount *</label>
        <div class="input-group">
          <span class="input-group-text">$</span>
          <input
            v-model="item.amount"
            :class="['form-control', isCompanyBalanceError ? 'is-invalid' : '']"
            type="number"
            step="0.01"
            :id="`withdraw-request-amount-${index}`"
            placeholder="Enter Amount"
            :disabled="isAmountDisabled(item)"
            @input="onAmountChange($event.target.value, index)"
            :max="UNRESTRICTED_BALANCE_ENTITY_TYPES.includes(item.entity_type) ? undefined : item.balance"
            required
          />
        </div>
      </div>
      <div v-if="canUseTopUpAsap" class="col-auto d-flex align-items-end pl-0">
        <the-switcher
          v-model="item.is_asap"
          label="ASAP"
          :id="`withdraw-request-${index}-asap`"
          :disabled="loading"
          class="mb-1"
        />
      </div>
      <div v-if="formData?.length > 1" class="col-auto d-flex align-items-end pl-0">
        <div class="input-height">
          <button class="form-icon-btn h-100" type="button" :disabled="loading" @click="removeItem(index)">
            <span class="fa-light fa-trash text-muted" />
          </button>
        </div>
      </div>
    </div>
    <div class="custom mt-4">
      <the-switcher
        v-model="item.withdraw_full_balance"
        label="Withdraw full ad account balance"
        :id="`withdraw-request-withdraw_full_balance-${index}`"
        :true-value="true"
        :false-value="false"
        :disabled="loading || (item.balance === 0 && !UNRESTRICTED_BALANCE_ENTITY_TYPES.includes(item.entity_type))"
        @change="onWithdrawFullBalance($event, index)"
      />
      <div v-if="isFullBalanceWithdraw(item)" class="custom mt-2 fs-13-px text-muted">
        Auto Top-Ups will be disabled after the request is completed.
      </div>
    </div>
    <alert-info-simple v-if="item.entity_id" class="custom mt-4 fs-13-px"
      >Available for withdrawal:
      <span @click="setAmount(item.balance, index)" class="pointer">{{ formatSum(item.balance) }}</span>
    </alert-info-simple>
    <disabled-account-error-alert v-if="item.is_disabled" class="custom mt-4" />
  </div>
  <button @click="addNewItem" class="btn btn-secondary btn-sm fs-14-px" type="button" :disabled="loading">
    Add Another Account
  </button>

  <company-balance-error-alert v-if="isCompanyBalanceError" @open-invoice-modal="openInvoiceModal" class="my-3" />
  <api-errors v-if="error && !isCompanyBalanceError" :error="error" class="mt-3" />
</template>
